/**
 * GPS Tracker - Sends user position to Traccar via OsmAnd protocol
 */

const TRACCAR_URL = 'https://gps.livefusion.app/osmand';
const SEND_INTERVAL_MS = 30000;

export class GPSTracker {
  constructor() {
    this._watchId = null;
    this._intervalId = null;
    this._username = null;
    this._lastPosition = null;
    this._batteryLevel = null;
    this._status = 'off'; // 'off' | 'sending' | 'no-permission' | 'unavailable'
    this._onStatusChange = null;

    this._initBattery();
  }

  get status() {
    return this._status;
  }

  set onStatusChange(fn) {
    this._onStatusChange = fn;
  }

  async _initBattery() {
    try {
      if (navigator.getBattery) {
        const battery = await navigator.getBattery();
        this._batteryLevel = Math.round(battery.level * 100);
        battery.addEventListener('levelchange', () => {
          this._batteryLevel = Math.round(battery.level * 100);
        });
      }
    } catch (e) {
      // Battery API not available
    }
  }

  start(username) {
    if (this._watchId !== null) return;

    if (!('geolocation' in navigator)) {
      this._setStatus('unavailable');
      console.warn('[GPS] Geolocation not available');
      return;
    }

    this._username = username;
    console.log('[GPS] Starting tracking for', username);
    this._setStatus('waiting');

    this._watchId = navigator.geolocation.watchPosition(
      (pos) => this._onPosition(pos),
      (err) => this._onError(err),
      {
        enableHighAccuracy: true,
        maximumAge: 30000,
        timeout: 30000
      }
    );

    // Send position every 30 seconds
    this._intervalId = setInterval(() => this._sendPosition(), SEND_INTERVAL_MS);
  }

  stop() {
    if (this._watchId !== null) {
      navigator.geolocation.clearWatch(this._watchId);
      this._watchId = null;
    }
    if (this._intervalId !== null) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
    this._lastPosition = null;
    this._setStatus('off');
    console.log('[GPS] Tracking stopped');
  }

  _onPosition(pos) {
    const isFirstFix = this._lastPosition === null;
    this._lastPosition = pos;
    this._setStatus('sending');

    // Send immediately on first fix
    if (isFirstFix) {
      this._sendPosition();
    }
  }

  _onError(err) {
    console.warn('[GPS] Error:', err.code, err.message);
    if (err.code === err.PERMISSION_DENIED) {
      this._setStatus('no-permission');
      this.stop();
    }
    // POSITION_UNAVAILABLE and TIMEOUT: watchPosition keeps retrying,
    // stay in 'waiting' status so user knows it's still trying
  }

  _sendPosition() {
    if (!this._lastPosition || !this._username) return;

    const { latitude, longitude, altitude, speed, heading, accuracy } = this._lastPosition.coords;
    const timestamp = Math.floor(this._lastPosition.timestamp / 1000);

    const params = new URLSearchParams({
      id: this._username,
      lat: latitude.toFixed(6),
      lon: longitude.toFixed(6),
      timestamp: timestamp.toString(),
      speed: (speed !== null ? speed * 1.944 : 0).toFixed(1), // m/s → knots (OsmAnd protocol)
      bearing: (heading !== null ? heading : 0).toFixed(1),
      altitude: (altitude !== null ? altitude : 0).toFixed(1),
      accuracy: (accuracy !== null ? accuracy : 0).toFixed(1)
    });

    if (this._batteryLevel !== null) {
      params.set('batt', this._batteryLevel.toString());
    }

    const url = `${TRACCAR_URL}/?${params.toString()}`;

    fetch(url, { mode: 'no-cors' }).catch((err) => {
      console.warn('[GPS] Send failed:', err.message);
    });
  }

  _setStatus(status) {
    if (this._status === status) return;
    this._status = status;
    console.log('[GPS] Status:', status);
    if (this._onStatusChange) {
      this._onStatusChange(status);
    }
  }
}
